import React from 'react';
import PropTypes from 'prop-types';
import { Button as MUIButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import * as R from 'ramda';

import { colors } from '@constants/colors';


// box shadow used for Button in MUI 0.4
const MUI_0_BUTTON_BOX_SHADOW =
  'rgb(0 0 0 / 12%) 0px 1px 6px, rgb(0 0 0 / 12%) 0px 1px 4px';

// v9 removed every combined {variant}{Color} utility class (containedPrimary, textInherit,
// textPrimary, outlinedInherit, ...) from Button's own class list — it now only generates
// separate variant (outlined/text/contained) and color (colorPrimary/colorInherit/...) classes.
// Passing classes={{outlinedInherit: ...}} etc. is silently ignored now, since MUI's
// composeClasses no longer recognizes those slot names at all (they never reach the DOM).
// Reimplemented as compound selectors nested inside `root` (still a real, recognized slot),
// matching on both classes being present together instead of relying on a combined slot name.
// Every fix below is scoped with `:not(.Mui-disabled)`: without it, each compound selector
// (2-3 classes) has higher specificity than MUI's own `.MuiButtonBase-root.Mui-disabled` (2
// classes) disabled style, so our color would win and a disabled button would render fully
// opaque/colored instead of MUI's greyed-out disabled look, even though the actual `disabled`
// attribute (and click-blocking) is unaffected.
const colorVariantFixes = {
  '&.MuiButton-contained.MuiButton-colorPrimary:not(.Mui-disabled)': {
    boxShadow: MUI_0_BUTTON_BOX_SHADOW,
    '&:hover': {
      boxShadow: MUI_0_BUTTON_BOX_SHADOW
    },
    color: colors.neutral.white,
    backgroundColor: colors.brand.blue
  },
  '&.MuiButton-text.MuiButton-colorInherit:not(.Mui-disabled)': {
    color: colors.neutral.black
  },
  '&.MuiButton-text.MuiButton-colorPrimary:not(.Mui-disabled)': {
    color: colors.brand.blue
  },
  '&.MuiButton-outlined.MuiButton-colorInherit:not(.Mui-disabled)': {
    border: `1px solid ${colors.neutral.silver}`
  }
};

const useStyles = makeStyles()((theme, { style, component }) => ({
  root: {
    borderRadius: '2px',
    minWidth: '88px',
    padding: '6px 8px',
    whiteSpace: 'nowrap',
    component,
    ...style,
    ...colorVariantFixes
  },
  plainRoot: colorVariantFixes
}));

export const Button = ({ color, plain = false, ...props }) => {
  const { classes } = useStyles(props, {
    props
  });

  // Renders a plain, unstyled MUI Button, opting out of this wrapper's app-wide default
  // styling (min-width, padding, border-radius, custom colors) for one-off cases that need
  // to match plain MUI Button appearance exactly. The color default below matches the
  // non-plain branch: v5 removed the 'default' (neutral grey) color option MUI v4 had,
  // defaulting instead to 'primary' (blue) — 'inherit' is the closest available match.
  // The outlinedInherit class restores v4's fixed neutral outlined border (v4 always drew
  // rgba(0,0,0,0.23) for an outlined button with no explicit color); v5's own outlined+inherit
  // style instead uses `border: 1px solid currentColor`, which resolves to whatever text
  // color the button happens to inherit from its surroundings, not a guaranteed visible grey.
  if (plain) {
    return (
      <MUIButton
        {...R.omit(['textColor'], props)}
        classes={{ root: classes.plainRoot, ...props.classes }}
        color={color ?? 'inherit'}
      />
    );
  }

  return (
    <MUIButton
      {...R.omit(['textColor'], props)}
      classes={{
        root: classes.root,
        ...props.classes
      }}
      color={color ?? 'inherit'}
    />
  );
};

Button.propTypes = {
  classes: PropTypes.object,
  color: PropTypes.string,
  plain: PropTypes.bool
};

export default Button;
